import { Component, Input, OnInit, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { ImpressoraDTO } from '../../../models/impressora.models';
import { ImpressoraService } from '../../../services/impressora/impressora.service';

/**
 * Seletor de impressora com cadastro rapido inline, espelhando `SelectMarcaComponent`.
 *
 * Consolida as tres implementacoes que existiam soltas — duas em
 * `configuracao-da-empresa` (venda e pedido) e uma em `lista-de-produtos` — e que ja haviam
 * divergido, cada uma com um defeito proprio:
 *
 * - `lista-de-produtos` usava `codigoImpressora` como valor do dropdown, enquanto o backend
 *   resolve por PK (`ItemFormMapper.findById`);
 * - `lista-de-produtos` nao paginava, truncando a lista em 20 impressoras em silencio;
 * - o campo de PEDIDO da configuracao copiou o markup sem o `(onChange)` e sem o template do
 *   "Adicionar", entao clicar nele gravava a String `'adicionar'` no `ngModel`.
 *
 * O botao "Adicionar" fica no `pTemplate="footer"`, e nao como item falso da lista. Essa e a
 * diferenca estrutural: um footer nao pode virar valor do `ngModel`, entao a classe de defeito
 * do `'adicionar'` deixa de existir.
 *
 * O valor emitido e SEMPRE o `id` (PK) da impressora, nunca o `codigoImpressora`.
 */
@Component({
  selector: 'app-select-impressora',
  templateUrl: './select-impressora.component.html',
  styleUrls: ['./select-impressora.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SelectImpressoraComponent),
      multi: true,
    },
  ],
})
export class SelectImpressoraComponent implements ControlValueAccessor, OnInit {

  /* ======================= *
   * Inputs
   * ======================= */
  @Input() placeholder = '';
  @Input() showAddButton = true;
  @Input() showClear = true;
  /** Repassado ao `p-dropdown`, para casos em que o seletor vive dentro de um dialog. */
  @Input() appendTo: any = null;
  @Input() inputId = '';

  /* ======================= *
   * Estado interno
   * ======================= */
  impressoras: ImpressoraDTO[] = [];
  loading = false;

  /** PK da impressora selecionada. */
  value: number | null = null;

  dialogVisible = false;

  /* ======================= *
   * ControlValueAccessor
   * ======================= */
  onChange: (value: any) => void = () => { };
  onTouched: () => void = () => { };

  constructor(private impressoraService: ImpressoraService) { }

  ngOnInit(): void {
    this.carregarImpressoras();
  }

  writeValue(value: any): void {
    this.value = value ?? null;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  onSelect(value: any): void {
    this.value = value ?? null;
    this.onChange(this.value);
  }

  /**
   * `GET /impressora` e paginado com `size=20` por padrao no backend. Sem `size` explicito a
   * lista truncava em silencio a partir da 21a impressora — era o defeito da tela de produtos.
   */
  private carregarImpressoras(): void {
    this.loading = true;
    this.impressoraService.get('/impressora', { page: 0, size: 100, sort: 'nome,asc' }).subscribe({
      next: (res: any) => {
        this.impressoras = Array.isArray(res) ? res : (res?.content || []);
        this.loading = false;
      },
      error: () => {
        // A impressora e opcional em todos os formularios que usam este seletor: falhar em
        // silencio e melhor do que bloquear o cadastro do item ou da empresa.
        this.impressoras = [];
        this.loading = false;
      },
    });
  }

  /**
   * Handler do footer "Adicionar". Usa `mousedown` com `stopPropagation` para o dropdown nao
   * fechar antes de o clique ser processado — mesmo motivo do `select-marca`.
   */
  onAddNew(event: MouseEvent): void {
    event.stopPropagation();
    this.dialogVisible = true;
  }

  /** Insere a impressora recem-criada no topo da lista e ja a seleciona. */
  onImpressoraCriada(impressora: ImpressoraDTO): void {
    this.impressoras = [impressora, ...this.impressoras];
    this.value = impressora.id ?? null;
    this.onChange(this.value);
  }
}
